// Shared property-feed helpers for the "Your listings" dashboard and each
// property's space page. The /auth/me/reports/ feed is one row per prep
// (newest-first); the durable object agents think in is the PROPERTY, so both
// surfaces group rows by normalized address.

// Where "Finish preparing" / "Try again" land: the confirmation page reads the
// listing from session storage (`currentListingId`), so every consumer that
// links here must seed it first (see `seedsListing`).
export const CONFIRMATION_PATH = "/listing-confirmation/";

// Dashboard status -> pill label/colors (server-truthful; see my_reports).
export const PROPERTY_STATUS = {
  // Started in the funnel but never confirmed. Distinct from "preparing" on
  // purpose: nothing is being generated, and the agent can still finish
  // preparing it.
  unfinished: { label: "Unfinished", $bg: "#f3f4f6", $fg: "#4b5563" },
  preparing: { label: "Preparing", $bg: "#fef3c7", $fg: "#92400e" },
  awaiting_review: { label: "Quality check", $bg: "#ffedd5", $fg: "#9a3412" },
  needs_attention: { label: "Needs attention", $bg: "#fee2e2", $fg: "#991b1b" },
  refunded: { label: "Refunded", $bg: "#f3f4f6", $fg: "#4b5563" },
  ready: { label: "Ready", $bg: "#dbeafe", $fg: "#1e40af" },
  personalized: { label: "Personalized", $bg: "#ede9fe", $fg: "#5b21b6" },
  shared: { label: "Shared", $bg: "#dcfce7", $fg: "#166534" },
};

/** @typedef {keyof typeof PROPERTY_STATUS} KnownPropertyStatus */

/**
 * A row returned by `/auth/me/reports/`.
 *
 * @typedef {object} ReportSummary
 * @property {string} computation_uuid
 * @property {string | null} [listing_uuid]
 * @property {string | null} [address]
 * @property {string | null} [status]
 * @property {boolean} [editable]
 * @property {string | null} [report_pdf_url]
 * @property {string | null} [hero_image_url]
 * @property {string | null} [touchup_url]
 * @property {string | null} [created_at]
 * @property {string | null} [edited_at]
 * @property {string | null} [shared_at]
 * @property {Record<string, { url?: string | null, available?: boolean }> | null} [artifacts]
 */

/**
 * @typedef {object} PropertyGroup
 * @property {string} key
 * @property {ReportSummary} latest
 * @property {ReportSummary[]} earlier
 */

/** @typedef {{ href: string | null, label: string | null, external: boolean }} PrimaryAction */

// Credits (pricing v2): a failed run's charge comes back by itself. The feed
// row carries no funding field, so a credits listing and a legacy cash one
// look the same here — the line is phrased to be true for both.
export const REFUND_REASSURANCE =
  "If anything was charged for this run, it was returned automatically.";

const NEEDS_ATTENTION_NOTICE =
  "We hit a problem preparing this listing. Our team has been notified and is on it.";

const SUPPORT_NOTICES = {
  awaiting_review: "We're checking this prep before release.",
  // Only reachable once the automatic retry has been spent — the API re-runs a
  // failed prep once on its own, and while that runs the status is "preparing"
  // again, so an agent never sees this for a blip. By the time it shows, the
  // API has emailed the team, and saying so is the difference between "your
  // order broke" and "your order broke and someone knows".
  needs_attention: `${NEEDS_ATTENTION_NOTICE} ${REFUND_REASSURANCE}`,
  // "refunded" is the cash refund state (a Stripe refund flips the listing);
  // a credits refund is a ledger entry and the listing reads needs_attention.
  refunded: "This order was refunded.",
};

/** @param {string | null | undefined} status */
export const isPollingStatus = (status) => status === "preparing" || status === "awaiting_review";

/**
 * Treat every non-empty, unknown status as support-only (fail closed).
 * @param {string | null | undefined} status
 */
export const isSupportOnlyStatus = (status) => {
  if (!status) return false;
  // "unfinished" is self-service, not a support case: the agent simply never
  // finished preparing the listing and can do so themselves.
  return !["unfinished", "preparing", "ready", "personalized", "shared"].includes(status);
};

/**
 * Keep unknown server values visible and safely non-actionable.
 * @param {string | null | undefined} status
 */
export const propertyStatusMeta = (status) => {
  if (status && Object.prototype.hasOwnProperty.call(PROPERTY_STATUS, status)) {
    return PROPERTY_STATUS[/** @type {KnownPropertyStatus} */ (status)];
  }
  return PROPERTY_STATUS.needs_attention;
};

/** @param {string | null | undefined} status */
export const supportNoticeFor = (status) => {
  if (!status || !isSupportOnlyStatus(status)) return null;
  if (Object.prototype.hasOwnProperty.call(SUPPORT_NOTICES, status)) {
    return SUPPORT_NOTICES[/** @type {keyof typeof SUPPORT_NOTICES} */ (status)];
  }
  return "This prep needs a quick review. Contact support for help.";
};

// USPS-style suffix equivalences. Real feeds carry the same house three ways
// ("47 Corey Ln", "47 Corey Lane"), and locality varies too ("Mendham" vs
// "Mendham Township"), so a whole-string comparison split one property into
// three cards on the dashboard that promises one card per property.
/** @type {Record<string, string>} */
const STREET_SUFFIXES = {
  street: "st",
  avenue: "ave",
  av: "ave",
  road: "rd",
  drive: "dr",
  lane: "ln",
  court: "ct",
  place: "pl",
  terrace: "ter",
  boulevard: "blvd",
  circle: "cir",
  trail: "trl",
  parkway: "pkwy",
  highway: "hwy",
  cove: "cv",
  square: "sq",
  crossing: "xing",
  heights: "hts",
  junction: "jct",
  landing: "lndg",
  mount: "mt",
  point: "pt",
  ridge: "rdg",
  river: "riv",
  station: "sta",
  valley: "vly",
  north: "n",
  south: "s",
  east: "e",
  west: "w",
  northeast: "ne",
  northwest: "nw",
  southeast: "se",
  southwest: "sw",
  apartment: "apt",
  suite: "ste",
  unit: "unit",
};

const collapse = (/** @type {string} */ text) =>
  text.toLowerCase().replace(/[.,#]/g, " ").replace(/\s+/g, " ").trim();

/** Normalize an address so re-runs / re-submissions of the same property merge
 * into one card.
 *
 * Keys on street line + ZIP, with suffixes and directionals folded to their
 * USPS abbreviations. City/state are deliberately excluded: the ZIP already
 * pins the locality, and the same house is filed under both the town and the
 * township. Falls back to the whole collapsed string when there is no ZIP, so
 * two different towns can never merge on street name alone.
 * @param {string | null | undefined} addr
 */
export const normalizeAddress = (addr) => {
  const whole = collapse(addr || "");
  if (!whole) return "";
  const zip = whole.match(/\b(\d{5})(?:-\d{4})?\b(?!.*\b\d{5}\b)/);
  if (!zip) return whole;
  const streetLine = collapse(String(addr).split(",")[0] || "");
  if (!streetLine) return whole;
  const street = streetLine
    .split(" ")
    .map((token) => STREET_SUFFIXES[token] || token)
    .join(" ");
  return `${street}|${zip[1]}`;
};

/** Group the flat reports feed (one row per listing, newest-first) into
 * properties keyed by normalized address. The newest row is the card; older
 * rows for the same address become its "earlier preps". Falls back to a unique
 * key when the address is blank so nothing silently merges.
 * @param {ReportSummary[]} reports
 * @returns {PropertyGroup[]}
 */
export const groupByProperty = (reports) => {
  /** @type {Map<string, ReportSummary[]>} */
  const map = new Map();
  for (const report of reports) {
    const key =
      normalizeAddress(report.address) || `id:${report.listing_uuid || report.computation_uuid}`;
    const group = map.get(key);
    if (group) group.push(report);
    else map.set(key, [report]);
  }
  return Array.from(map.entries()).map(([key, group]) => ({
    key,
    latest: group[0],
    earlier: group.slice(1),
  }));
};

/** Find the property group containing the given listing uuid (any prep row —
 * latest or earlier — matches, since re-runs carry different listing uuids).
 * @param {PropertyGroup[]} properties
 * @param {string} listingUuid
 */
export const findPropertyByListing = (properties, listingUuid) =>
  properties.find((property) =>
    [property.latest, ...property.earlier].some(
      (report) => report.listing_uuid === listingUuid || report.computation_uuid === listingUuid
    )
  ) || null;

/** The ONE destination for a prep by state: Personalize / Share / View (PDF).
 * Returns null fields while preparing, support-only, or for legacy rows with
 * nothing to open.
 * @param {ReportSummary} latest
 * @returns {PrimaryAction}
 */
export const primaryActionFor = (latest) => {
  const editUrl = `/reports/${latest.computation_uuid}/edit`;
  const shareUrl = `/reports/${latest.computation_uuid}/share`;
  if (latest.status === "unfinished") {
    // The Prepare sheet was never completed — send them back to finish it
    // rather than stranding the listing on the dashboard.
    return {
      href: CONFIRMATION_PATH,
      label: "Finish preparing",
      external: false,
    };
  }
  if (latest.status === "needs_attention" && latest.listing_uuid) {
    // A failed credits run was refunded and keeps its free re-prepare; the
    // agent can run it again herself instead of writing to support.
    return { href: CONFIRMATION_PATH, label: "Try again", external: false };
  }
  if (latest.status === "preparing" || isSupportOnlyStatus(latest.status)) {
    return { href: null, label: null, external: false };
  }
  if (latest.status === "ready") {
    if (latest.editable) return { href: editUrl, label: "Personalize", external: false };
    if (latest.report_pdf_url) {
      return { href: latest.report_pdf_url, label: "View", external: true };
    }
  } else if (latest.status === "personalized") {
    if (latest.editable) return { href: shareUrl, label: "Share", external: false };
    if (latest.report_pdf_url) {
      return { href: latest.report_pdf_url, label: "View", external: true };
    }
  } else if (latest.status === "shared") {
    return { href: shareUrl, label: "Share", external: false };
  }
  return { href: null, label: null, external: false };
};

/** Whether an action routes to the confirmation page, which reads the listing
 * from session storage — the caller must seed `currentListingId` first.
 * @param {PrimaryAction} action
 */
export const seedsListing = (action) => action.href === CONFIRMATION_PATH;

/** The "files ready" fragment on a delivered card. Only bought files exist,
 * and the row's artifact manifest says which — never promise a file that was
 * not rendered.
 * @param {ReportSummary} latest
 * @returns {string} "" when nothing can be claimed.
 */
export const readyFilesLine = (latest) => {
  const artifacts = latest.artifacts || {};
  const names = /** @type {string[]} */ ([]);
  if (artifacts.report_pdf?.available) names.push("PDF");
  if (artifacts.deck?.available) names.push("slides");
  if (artifacts.one_pager?.available) names.push("one-pager");
  if (names.length === 0) return "";
  if (names.length === 1) return `${names[0]} ready`;
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]} ready`;
};

/** Shared once but edited since: the pill still says "Shared" (server truth),
 * so callers show a light indicator that the latest touches haven't gone out.
 * @param {ReportSummary} latest
 */
export const hasUnsharedEdits = (latest) =>
  !!latest.shared_at &&
  !!latest.edited_at &&
  new Date(latest.edited_at) > new Date(latest.shared_at);

/** @param {string | null | undefined} iso */
export const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};
